import {
  AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, Optional, QueryList, ViewChildren,
} from '@angular/core';
import { Subject, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, HeatmapBox, ReportBlock } from '../report-aggregation';
import { HeatmapImageStore } from './heatmap-images';

/** Cells along the image's longer side. Fine enough to follow a box's edge, coarse enough to paint at once. */
const CELLS_ON_LONG_SIDE = 200;

type Rgb = [number, number, number];

/**
 * Cool to hot. The one multi-hue scale in the deck, and deliberately not the client's palette:
 * "heat" is the reading people bring to this chart, and the scale printed beside it says so.
 */
// Starts at a muted grey, so a spot one or two people marked sits back rather than reading as a
// finding -- the scale the client's own heatmaps use.
const HEAT_STOPS: readonly { at: number; rgb: Rgb }[] = [
  { at: 0, rgb: [118, 118, 140] },
  { at: 0.18, rgb: [40, 44, 200] },
  { at: 0.42, rgb: [0, 180, 235] },
  { at: 0.62, rgb: [110, 215, 90] },
  { at: 0.8, rgb: [250, 228, 60] },
  { at: 1, rgb: [235, 50, 35] },
];

/**
 * How opaque the shading starts. The colours are painted solid and the reader's slider is the only
 * transparency, so 100% covers the creative completely. Strong enough to read over a busy photo.
 */
const DEFAULT_OPACITY = 70;

/**
 * Kept clear at the foot of the scroll area: the deck's page bar rides there, over the content, and
 * a card fitted to the full height would end underneath it.
 */
const BOTTOM_BAR_ALLOWANCE = 72;

/** The smallest an image is shrunk to, however little room is left -- below this it is unreadable. */
const MIN_STAGE_HEIGHT = 160;

/** How many respondents marked each cell of an image, cut into a grid of square cells. */
export interface HeatmapDensity {
  cols: number;
  rows: number;
  /** The image pixels along each side of one cell. */
  cell: number;
  /** Respondents covering each cell, row by row. */
  counts: Uint32Array;
  /** The most respondents on any one cell. */
  peak: number;
}

/**
 * Counts, for every cell, the respondents whose boxes cover it. A respondent counts once per cell
 * however many of their boxes overlap there, so a cell's count is a number of people and its share
 * of the base can never pass 100%.
 */
export function heatmapDensity(
  respondents: readonly HeatmapBox[][],
  width: number,
  height: number,
): HeatmapDensity {
  const cell = Math.max(1, Math.ceil(Math.max(width, height) / CELLS_ON_LONG_SIDE));
  const cols = Math.max(1, Math.ceil(width / cell));
  const rows = Math.max(1, Math.ceil(height / cell));
  const counts = new Uint32Array(cols * rows);
  // The last respondent to reach each cell, which is what keeps overlapping boxes from one person
  // to a single count.
  const reachedBy = new Int32Array(cols * rows).fill(-1);
  let peak = 0;

  respondents.forEach((boxes, respondent) => {
    for (const box of boxes) {
      if (box.right < 0 || box.bottom < 0 || box.left >= width || box.top >= height) continue;
      // A box narrower than a cell still covers the cell it sits in.
      const firstCol = Math.max(0, Math.floor(box.left / cell));
      const lastCol = Math.min(cols - 1, Math.max(firstCol, Math.ceil(box.right / cell) - 1));
      const firstRow = Math.max(0, Math.floor(box.top / cell));
      const lastRow = Math.min(rows - 1, Math.max(firstRow, Math.ceil(box.bottom / cell) - 1));

      for (let row = firstRow; row <= lastRow; row++) {
        for (let col = firstCol; col <= lastCol; col++) {
          const index = row * cols + col;
          if (reachedBy[index] === respondent) continue;
          reachedBy[index] = respondent;
          counts[index]++;
          if (counts[index] > peak) peak = counts[index];
        }
      }
    }
  });

  return { cols, rows, cell, counts, peak };
}

/** The heat scale's colour at `t`, from 0 (coolest) to 1 (hottest). */
export function heatColour(t: number): Rgb {
  const at = Math.min(1, Math.max(0, t));
  const upper = HEAT_STOPS.findIndex(stop => stop.at >= at);
  if (upper <= 0) return HEAT_STOPS[0].rgb;
  const from = HEAT_STOPS[upper - 1];
  const to = HEAT_STOPS[upper];
  const mix = (at - from.at) / (to.at - from.at);
  return from.rgb.map((channel, i) => Math.round(channel + (to.rgb[i] - channel) * mix)) as Rgb;
}

/** Where on the image a point is, in words, for the screen-reader summary. */
export function regionOf(across: number, down: number): string {
  const vertical = down < 1 / 3 ? 'top' : down < 2 / 3 ? 'middle' : 'bottom';
  const horizontal = across < 1 / 3 ? 'left' : across < 2 / 3 ? 'centre' : 'right';
  if (vertical === 'middle' && horizontal === 'centre') return 'the centre';
  return `the ${vertical} ${horizontal}`;
}

function percent(count: number, base: number): number {
  return base > 0 ? Math.round((count / base) * 100) : 0;
}

/** One click category, drawn as its own copy of the image. */
interface Panel {
  name: string;
  base: number;
  density: HeatmapDensity | null;
  /** The count the hottest colour stands for: this panel's busiest cell, or the busiest of all. */
  scalePeak: number;
  scalePercent: number;
}

/** Which image a block shows: an upload it names, or else its question's own. */
interface ImageSource {
  question: string;
  imageId: string;
}

/** What the pointer is over: which panel, where to put the readout, and what it says. */
interface Readout {
  panel: number;
  left: number;
  top: number;
  text: string;
}

/**
 * Heatmap: where respondents marked an image, shaded over the image itself.
 *
 * The shading is a share of respondents, not a count of boxes: each cell is shaded by how many of
 * the people who answered drew over it. It is painted onto a canvas at one pixel per cell and
 * stretched over the image, so the browser's own smoothing softens the cell edges without moving
 * any figure the readout reports.
 *
 * **Each click category is its own copy of the image, side by side** -- "likes" beside "dislikes".
 * By default each is coloured on its own scale, so a rarer reaction's busiest spot is as hot as a
 * common one's, and each panel then carries its own legend so the colours are never read across.
 * `sharedScale` puts every panel on the busiest panel's scale, with one legend for all of them.
 *
 * The boxes are in the creative's own pixels, so its natural size sets the grid. With no image, or
 * one that fails to load, the grid covers the furthest box anyone drew and sits on a plain panel.
 */
@Component({
  selector: 'app-heatmap-block',
  standalone: false,
  template: `
    @if (peak) {
      <!-- How strongly the shading covers the creative, so what was clicked can be read against it. -->
      <div class="overlay-controls d-flex align-items-center justify-content-end mb-1">
        <input type="range" class="overlay-opacity" min="0" max="100" step="5"
               aria-label="Heatmap opacity"
               [attr.aria-valuetext]="overlayOpacity + '%'"
               [value]="overlayOpacity"
               (input)="setOverlayOpacity($any($event.target).valueAsNumber)">
        <span class="overlay-opacity-value ml-2" aria-hidden="true">{{ overlayOpacity }}%</span>
        <button type="button"
                class="overlay-toggle btn btn-sm btn-link p-0 ml-3 shadow-none"
                (click)="toggleOverlay()">{{ overlayShown ? 'Hide' : 'Show' }}</button>
      </div>
    }

    <div class="panels" [class.compact]="compact">
      @for (panel of panels; track $index) {
        <figure class="panel mb-0">
          @if (panels.length > 1) {
            <figcaption class="panel-name mb-1">{{ panel.name }}</figcaption>
          }
          <!-- Drawn for an image even before there is anything to size it by: the image has to be on
               the page to load, and its loaded size is what sizes the grid. -->
          @if (showsImage || (width && height)) {
            <div class="stage"
                 [class.is-blank]="!showsImage"
                 [style.aspect-ratio]="width + ' / ' + height"
                 (pointermove)="point($index, $event)"
                 (pointerleave)="readout = null">
              @if (showsImage) {
                <img class="creative" [src]="imageSrc" [alt]="altFor(panel)"
                     (load)="imageLoaded($event)" (error)="imageFailed()">
              }
              <canvas #overlay class="overlay" [class.is-hidden]="!overlayVisible"
                      [style.opacity]="overlayOpacity / 100" aria-hidden="true"></canvas>
              @if (overlayVisible && readout && readout.panel === $index) {
                <div class="readout nav-font12" aria-hidden="true"
                     [class.from-left]="readout.left < 20" [class.from-right]="readout.left > 80"
                     [style.left.%]="readout.left" [style.top.%]="readout.top">{{ readout.text }}</div>
              }
            </div>
          }
          @if (!oneLegend && panel.scalePeak) {
            <ng-container *ngTemplateOutlet="legend; context: { $implicit: panel.scalePercent }"></ng-container>
          }
        </figure>
      }
    </div>

    @if (oneLegend && peak) {
      <ng-container *ngTemplateOutlet="legend; context: { $implicit: peakPercent }"></ng-container>
    }

    <!-- Hidden with the shading rather than removed, so turning it off never resizes the image. -->
    <ng-template #legend let-top>
      <div class="legend mt-2" aria-hidden="true" [class.is-hidden]="!overlayVisible">
        <!-- At the shading's own opacity, so the key matches what is on the image. -->
        <span class="ramp" [style.background]="ramp" [style.opacity]="overlayOpacity / 100"></span>
        <span class="d-flex justify-content-between">
          <span class="grey4 nav-font12">Least selected</span>
          <span class="grey4 nav-font12">Most selected ({{ top }}%)</span>
        </span>
      </div>
    </ng-template>

    @for (note of notes; track note) {
      <p class="grey4 nav-font12 mb-0 mt-1">{{ note }}</p>
    }

    <p class="sr-only">{{ summary }}</p>
  `,
  styles: [`
    :host { display: block; }

    .panels {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }
    /* Side by side where the card has room for two, one above the other where it does not. */
    .panel {
      flex: 1 1 240px;
      min-width: 0;
    }
    .panels.compact .panel { flex-basis: 160px; }

    .panel-name {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-sm, 12px);
      font-weight: 700;
    }

    /* Capped through its width, which the aspect ratio turns into a height -- see fitToView. Centred,
       because a tall image shrunk to fit is narrower than its panel. */
    .stage {
      position: relative;
      width: 100%;
      max-width: var(--heatmap-stage-max-width, none);
      margin-left: auto;
      margin-right: auto;
    }
    .stage.is-blank {
      background-color: transparent;
    }

    /* Stretched to the stage rather than sized itself, so the image and the grid over it are the
       same box whatever the card's width. */
    .creative,
    .overlay {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .overlay { pointer-events: none; }
    /* Hidden rather than removed, so turning it back on needs no repaint. */
    .overlay.is-hidden { visibility: hidden; }

    .overlay-controls {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-sm, 12px);
    }
    .overlay-opacity {
      width: 120px;
      accent-color: var(--ecr-secondary, #1F7A8C);
    }
    /* Wide enough for "100%", so the slider doesn't shift as the number changes. */
    .overlay-opacity-value {
      min-width: 4ch;
      text-align: right;
    }
    .overlay-toggle {
      color: var(--ecr-secondary, #1F7A8C);
      font-size: var(--ecr-font-sm, 12px);
    }

    .readout {
      position: absolute;
      transform: translate(-50%, calc(-100% - 8px));
      pointer-events: none;
      white-space: nowrap;
      background-color: var(--ecr-card, #ffffff);
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: var(--ecr-radius-sm, 4px);
      padding: 2px 6px;
      color: var(--ecr-ink, #26414c);
    }
    /* Near either edge the readout hangs inward from the pointer, or half of it would leave the card. */
    .readout.from-left { transform: translate(0, calc(-100% - 8px)); }
    .readout.from-right { transform: translate(-100%, calc(-100% - 8px)); }

    /* Centred under the image, its two ends named rather than numbered: the readout on hover gives
       the exact figure, and the scale only has to say which way it runs. */
    .legend {
      display: flex;
      flex-direction: column;
      width: 240px;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
    .legend.is-hidden { visibility: hidden; }
    .ramp {
      display: block;
      height: 10px;
      border-radius: 2px;
      margin-bottom: 2px;
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `],
})
export class HeatmapBlockComponent implements ReportBlock, OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  @Input() editing?: boolean;

  // A setter, because the canvases come and go with the panels around them: they are created after
  // the build that first gives the grid a size, and that build had nothing to paint on.
  @ViewChildren('overlay') set overlayRefs(refs: QueryList<ElementRef<HTMLCanvasElement>>) {
    this.overlays = refs.toArray();
    this.paint();
    this.scheduleFit();
  }
  private overlays: ElementRef<HTMLCanvasElement>[] = [];

  panels: Panel[] = [];
  width = 0;
  height = 0;
  /** The busiest cell in any panel. */
  peak = 0;
  peakPercent = 0;
  /** One legend under every panel, where they share a scale or there is only one of them. */
  oneLegend = true;
  readonly ramp = `linear-gradient(to right, ${HEAT_STOPS.map(stop => {
    const [red, green, blue] = stop.rgb;
    return `rgb(${red}, ${green}, ${blue}) ${stop.at * 100}%`;
  }).join(', ')})`;
  notes: string[] = [];
  summary = '';
  readout: Readout | null = null;
  /**
   * The reader's own setting, 0 to 100, so the creative can be seen through the shading or without it.
   * Not saved.
   */
  overlayOpacity = DEFAULT_OPACITY;
  /** What Show goes back to after Hide, so hiding and showing again keeps the reader's setting. */
  private opacityBeforeHide = DEFAULT_OPACITY;

  get overlayShown(): boolean {
    return this.overlayOpacity > 0;
  }

  /** The image to draw on, as an object URL, once the store has found one. */
  imageSrc = '';
  /** Whether there is an image at all, which only the store can say -- and not straight away. */
  private imageState: 'pending' | 'none' | 'found' = 'pending';

  /** The creative's own size once it has loaded; until then the boxes decide the grid. */
  private natural: { width: number; height: number } | null = null;
  private loadFailed = false;

  private readonly imageSource$ = new Subject<ImageSource>();
  private readonly destroy$ = new Subject<void>();

  private resizeObserver?: ResizeObserver;
  private fitFrame = 0;

  // Both optional: a block can be drawn where no page provides the store -- a test, a preview -- and
  // then simply has no image; and a test builds it with no element to measure.
  constructor(
    @Optional() images: HeatmapImageStore | null,
    @Optional() private host: ElementRef<HTMLElement> | null = null,
  ) {
    this.imageSource$.pipe(
      distinctUntilChanged((a, b) => a.question === b.question && a.imageId === b.imageId),
      switchMap(source => images ? images.imageFor(source.question, source.imageId) : of(null)),
      takeUntil(this.destroy$),
    ).subscribe(url => this.showImage(url));
  }

  /**
   * Whether the shading is shown: the reader hasn't hidden it, and the image it sits on is settled --
   * loaded, or known to be missing or broken, when it goes on the blank panel instead. Drawn before
   * that, the shading appears on its own and the creative pops in under it, often at another size.
   */
  get overlayVisible(): boolean {
    if (!this.overlayShown || this.imageState === 'pending') return false;
    return !this.showsImage || !!this.natural;
  }

  get showsImage(): boolean {
    return !!this.imageSrc && !this.loadFailed;
  }

  /**
   * Shrinks the images until the whole card -- title, toggle, images, legend and notes -- fits in
   * the visible height of whatever scrolls the deck, so nothing of the module is cut off.
   *
   * Measured rather than estimated: what is not image (the card's chrome) is the card's height less
   * its images', so it holds however long the title or the notes run. Panels that wrap onto several
   * rows share the room. The cap is applied through the width, which the fixed aspect ratio turns
   * into a height; capping the height itself would stretch the image instead of shrinking it. Set as
   * a CSS variable on the host, so it needs no change detection and survives the stages re-rendering.
   */
  private fitToView() {
    const host = this.host?.nativeElement;
    if (!host || !this.width || !this.height) return;

    const stages = Array.from(host.querySelectorAll<HTMLElement>('.stage'));
    const stageHeight = stages[0]?.getBoundingClientRect().height ?? 0;
    if (!stageHeight) return;

    const rows = new Set(stages.map(stage => Math.round(stage.getBoundingClientRect().top))).size;
    const card = host.closest<HTMLElement>('.report-block') ?? host;
    const chrome = card.getBoundingClientRect().height - rows * stageHeight;
    const room = this.visibleHeight(host) - chrome;
    const maxHeight = Math.max(MIN_STAGE_HEIGHT, Math.floor(room / rows));

    host.style.setProperty('--heatmap-stage-max-width', `${maxHeight * (this.width / this.height)}px`);
  }

  /** The height the deck shows at once: its scroll area's, less its padding and the page bar. */
  private visibleHeight(host: HTMLElement): number {
    for (let element = host.parentElement; element; element = element.parentElement) {
      const style = getComputedStyle(element);
      if (style.overflowY !== 'auto' && style.overflowY !== 'scroll') continue;
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      return element.clientHeight - padding - BOTTOM_BAR_ALLOWANCE;
    }
    return window.innerHeight - BOTTOM_BAR_ALLOWANCE;
  }

  ngOnChanges() {
    this.build();
    const question = this.series?.find(series => series.heatmap)?.heatmap?.question ?? '';
    const imageId = this.block.reportType === 'heatmap' ? this.block.reportExtraData.imageId ?? '' : '';
    this.imageSource$.next({ question, imageId });
  }

  ngAfterViewInit() {
    const host = this.host?.nativeElement;
    if (!host || typeof ResizeObserver === 'undefined') return;
    // The card rather than the block: the title and the notes are outside this component and can
    // change its height too. A refit after its own shrink measures the same chrome, so it settles.
    this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
    this.resizeObserver.observe(host.closest('.report-block') ?? host);
  }

  // The window can get shorter without the card changing size at all.
  @HostListener('window:resize')
  onWindowResize() {
    this.scheduleFit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.fitFrame);
  }

  /** On the next frame, once the view has the stages a build just changed. */
  private scheduleFit() {
    if (!this.host || typeof requestAnimationFrame === 'undefined') return;
    cancelAnimationFrame(this.fitFrame);
    this.fitFrame = requestAnimationFrame(() => this.fitToView());
  }

  private showImage(url: string | null) {
    this.imageState = url ? 'found' : 'none';
    if ((url ?? '') !== this.imageSrc) {
      // A new image: whatever the last one measured, or failed to, no longer applies.
      this.imageSrc = url ?? '';
      this.natural = null;
      this.loadFailed = false;
    }
    this.build();
  }

  altFor(panel: Panel): string {
    return this.panels.length > 1
      ? `The image respondents were asked to mark: ${panel.name}`
      : 'The image respondents were asked to mark';
  }

  imageLoaded(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.naturalWidth || !image.naturalHeight) return;
    if (this.natural?.width === image.naturalWidth && this.natural.height === image.naturalHeight) return;
    this.natural = { width: image.naturalWidth, height: image.naturalHeight };
    this.build();
  }

  setOverlayOpacity(value: number) {
    if (Number.isNaN(value)) return;
    this.overlayOpacity = Math.min(100, Math.max(0, value));
    if (!this.overlayShown) this.readout = null;
  }

  /** Hide drops the shading to 0%; Show puts back the setting from before the last Hide. */
  toggleOverlay() {
    if (this.overlayShown) {
      this.opacityBeforeHide = this.overlayOpacity;
      this.overlayOpacity = 0;
      this.readout = null;
    } else {
      this.overlayOpacity = this.opacityBeforeHide || DEFAULT_OPACITY;
    }
  }

  imageFailed() {
    if (this.loadFailed) return;
    this.loadFailed = true;
    this.build();
  }

  /** The readout under the pointer, read off the same counts the shading was painted from. */
  point(index: number, event: PointerEvent) {
    const panel = this.panels[index];
    const density = panel?.density;
    if (!density || !this.overlayVisible) return;

    const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
    if (!box.width || !box.height) return;
    const across = Math.min(0.9999, Math.max(0, (event.clientX - box.left) / box.width));
    const down = Math.min(0.9999, Math.max(0, (event.clientY - box.top) / box.height));
    const col = Math.min(density.cols - 1, Math.floor((across * this.width) / density.cell));
    const row = Math.min(density.rows - 1, Math.floor((down * this.height) / density.cell));
    const count = density.counts[row * density.cols + col];
    const share = count
      ? `${percent(count, panel.base)}% marked here (${count} of ${panel.base})`
      : 'Nobody marked here';

    this.readout = {
      panel: index,
      left: across * 100,
      top: down * 100,
      text: this.panels.length > 1 ? `${panel.name}: ${share}` : share,
    };
  }

  private build() {
    this.readout = null;
    this.panels = [];
    this.notes = [];
    this.summary = '';
    this.peak = 0;
    this.peakPercent = 0;
    if (this.block.reportType !== 'heatmap') return;

    const drawn = (this.series ?? []).filter(series => !!series.heatmap);
    this.sizeGrid(drawn);

    this.panels = drawn.map(series => ({
      name: series.name,
      base: series.base,
      density: this.width && this.height
        ? heatmapDensity(series.heatmap?.respondents ?? [], this.width, this.height)
        : null,
      scalePeak: 0,
      scalePercent: 0,
    }));
    this.peak = Math.max(0, ...this.panels.map(panel => panel.density?.peak ?? 0));
    this.peakPercent = percent(this.peak, drawn[0]?.base ?? 0);

    const shared = !!this.block.reportExtraData.sharedScale;
    this.oneLegend = shared || this.panels.length < 2;
    for (const panel of this.panels) {
      panel.scalePeak = shared ? this.peak : panel.density?.peak ?? 0;
      panel.scalePercent = percent(panel.scalePeak, panel.base);
    }

    if (drawn.length) {
      this.notes = this.notesFor(drawn);
      this.summary = this.summaryFor();
    }
    this.paint();
    // The grid's shape or the notes may have changed what fits.
    this.scheduleFit();
  }

  /** The grid's coordinate space: the creative's own pixels, or the reach of the furthest box. */
  private sizeGrid(drawn: ChartSeries[]) {
    if (this.showsImage && this.natural) {
      this.width = this.natural.width;
      this.height = this.natural.height;
      return;
    }
    // Every panel, so the copies of the image are all the same size.
    let right = 0;
    let bottom = 0;
    for (const series of drawn) {
      for (const boxes of series.heatmap?.respondents ?? []) {
        for (const box of boxes) {
          right = Math.max(right, box.right);
          bottom = Math.max(bottom, box.bottom);
        }
      }
    }
    this.width = Math.ceil(right);
    this.height = Math.ceil(bottom);
  }

  private paint() {
    this.overlays.forEach((ref, index) => {
      const canvas = ref.nativeElement;
      const context = canvas.getContext('2d');
      if (!context) return;
      const panel = this.panels[index];
      const density = panel?.density;
      if (!density || !panel.scalePeak) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      canvas.width = density.cols;
      canvas.height = density.rows;
      const pixels = context.createImageData(density.cols, density.rows);
      density.counts.forEach((count, cell) => {
        if (!count) return;
        const t = count / panel.scalePeak;
        const [red, green, blue] = heatColour(t);
        const offset = cell * 4;
        pixels.data[offset] = red;
        pixels.data[offset + 1] = green;
        pixels.data[offset + 2] = blue;
        pixels.data[offset + 3] = 255;
      });
      context.putImageData(pixels, 0, 0);
    });
  }

  private notesFor(drawn: ChartSeries[]): string[] {
    const notes: string[] = [];
    // Every panel is the same question, so the base and the opt-out are the same on each.
    const [first] = drawn;
    const base = first.base;

    if (this.imageState === 'none' && this.editing) {
      notes.push('There is no image for this question yet. Upload one in this module\'s settings.');
    }
    if (this.imageSrc && this.loadFailed) {
      notes.push('The image could not be loaded, so the shading is shown on a blank panel.');
    }
    if (drawn.every(series => !series.heatmap?.respondents.length)) {
      notes.push('Nobody marked the image in the current view.');
    }

    const optOut = first.heatmap?.optOut;
    const showsOptOut = this.block.reportType === 'heatmap' && this.block.reportExtraData.showOptOut !== false;
    if (optOut && showsOptOut) {
      const label = optOut.label.replace(/[.\s]+$/, '');
      notes.push(`${percent(optOut.count, base)}% chose "${label}" (${optOut.count} of ${base}).`);
    }
    notes.push(`n=${base}`);
    return notes;
  }

  /** One sentence per panel, saying where its busiest spot is, for a reader without the picture. */
  private summaryFor(): string {
    return this.panels.map(panel => {
      const density = panel.density;
      if (!density || !density.peak) return `${panel.name}: nobody marked the image.`;

      // The busiest cells, averaged, so a spread of equally busy cells reads as their middle.
      let across = 0;
      let down = 0;
      let busiest = 0;
      density.counts.forEach((count, index) => {
        if (count !== density.peak) return;
        across += ((index % density.cols) + 0.5) / density.cols;
        down += (Math.floor(index / density.cols) + 0.5) / density.rows;
        busiest++;
      });

      return `${panel.name}: the most-marked area, towards ${regionOf(across / busiest, down / busiest)} `
        + `of the image, was marked by ${percent(density.peak, panel.base)}% of ${panel.base} respondents.`;
    }).join(' ');
  }
}
